/**
 * Wallet Disputes — shared constants.
 *
 * Status labels follow the written spec:
 *   UNDER_REVIEW → "Under Review"  (dispute/appeal submitted, CS investigating)
 *   DECLINED     → "Declined"      (CS rejected via Zoho; eligible to appeal)
 *   SETTLED      → "Settled"       (refund brought transacted amount <= client agreed)
 *   WITHDRAWN    → "Withdrawn"     (client cancelled before resolution — terminal)
 */

export const DISPUTE_STATUS = {
  UNDER_REVIEW: "UNDER_REVIEW",
  DECLINED: "DECLINED",
  SETTLED: "SETTLED",
  WITHDRAWN: "WITHDRAWN",
};

export const DISPUTE_STATUS_LABELS = {
  [DISPUTE_STATUS.UNDER_REVIEW]: "Under Review",
  [DISPUTE_STATUS.DECLINED]: "Declined",
  [DISPUTE_STATUS.SETTLED]: "Settled",
  [DISPUTE_STATUS.WITHDRAWN]: "Withdrawn",
};

/**
 * Non-terminal statuses. wallet-service rejects a new dispute on a booking room that
 * already has a dispute in one of these states (error OPEN_DISPUTE_EXISTS), so the UI
 * disables the entry-point Dispute button for these.
 */
export const OPEN_DISPUTE_STATUSES = [
  DISPUTE_STATUS.UNDER_REVIEW,
  DISPUTE_STATUS.DECLINED,
];

/**
 * Options for the disputes-list Status filter. Limited to the values the
 * wallet-service `DisputeStatus` enum accepts as a `status` query param —
 * WITHDRAWN is intentionally excluded (not a backend enum value; passing it
 * would 400 at the controller bind).
 */
export const DISPUTE_STATUS_FILTER_OPTIONS = [
  {
    label: DISPUTE_STATUS_LABELS[DISPUTE_STATUS.UNDER_REVIEW],
    value: DISPUTE_STATUS.UNDER_REVIEW,
  },
  {
    label: DISPUTE_STATUS_LABELS[DISPUTE_STATUS.DECLINED],
    value: DISPUTE_STATUS.DECLINED,
  },
  {
    label: DISPUTE_STATUS_LABELS[DISPUTE_STATUS.SETTLED],
    value: DISPUTE_STATUS.SETTLED,
  },
];

/**
 * Dispute History actions (history[].action from the detail endpoint).
 */
export const HISTORY_ACTION = {
  DISPUTE_SUBMITTED: "DISPUTE_SUBMITTED",
  APPEAL_SUBMITTED: "APPEAL_SUBMITTED",
  DISPUTE_DECLINED: "DISPUTE_DECLINED",
  DISPUTE_SETTLED: "DISPUTE_SETTLED",
  DISPUTE_WITHDRAWN: "DISPUTE_WITHDRAWN",
};

export const HISTORY_ACTION_LABELS = {
  [HISTORY_ACTION.DISPUTE_SUBMITTED]: "Dispute Submitted",
  [HISTORY_ACTION.APPEAL_SUBMITTED]: "Appeal Submitted",
  [HISTORY_ACTION.DISPUTE_DECLINED]: "Dispute Declined",
  [HISTORY_ACTION.DISPUTE_SETTLED]: "Dispute Settled",
  [HISTORY_ACTION.DISPUTE_WITHDRAWN]: "Dispute Withdrawn",
};

/** Actions submitted by an actual Wallet user (vs. system/admin). */
export const CLIENT_SUBMITTED_ACTIONS = [
  HISTORY_ACTION.DISPUTE_SUBMITTED,
  HISTORY_ACTION.APPEAL_SUBMITTED,
  HISTORY_ACTION.DISPUTE_WITHDRAWN,
];

/** Generic "submitted by" label for admin/system-side history entries. */
export const ADMIN_SUBMITTED_BY = "HotelTrader Admin";

/** Generic "submitted by" label for client-side history entries (API gives only a user id). */
export const CLIENT_SUBMITTED_BY = "Wallet user";

/**
 * Selectable Dispute Reasons (Submit + Appeal). `value` is the enum code sent to the API;
 * `label` is shown in the dropdown and rendered back in the history.
 */
export const DISPUTE_REASONS = [
  { value: "BOOKING_CANCELLED", label: "Booking Cancelled" },
  { value: "NO_SHOW", label: "No Show" },
  {
    value: "PRICE_DISCREPANCY_RESERVATION",
    label: "Price Discrepancy on Reservation in Client Site",
  },
  { value: "PRICE_DISCREPANCY_TAX", label: "Price Discrepancy on Tax only" },
  { value: "GUEST_CHECKED_OUT_EARLY", label: "Guest checked out early" },
  {
    value: "NO_RESERVATION_FOUND",
    label: "No Reservation found on client's site / Issue while traveling",
  },
  {
    value: "HOTEL_CONFIRMED_NO_CHARGE",
    label: "Hotel confirmed they're not charging (proof required)",
  },
  { value: "HOTEL_QUALITY_ISSUES", label: "Hotel Quality Issues" },
  { value: "RELOCATION_ISSUES", label: "Relocation Issues" },
];

/** Lookup: reason code → human label (for rendering history / detail). */
export const REASON_LABELS = DISPUTE_REASONS.reduce((acc, r) => {
  acc[r.value] = r.label;
  return acc;
}, {});

/** Reasons that require at least one supporting attachment (server: MISSING_REQUIRED_ATTACHMENT). */
export const ATTACHMENT_REQUIRED_REASONS = ["HOTEL_CONFIRMED_NO_CHARGE"];

/** Error shown next to a disabled Dispute button when one already exists. */
export const EXISTING_DISPUTE_MESSAGE =
  "A dispute has already been submitted for this booking";

/** Error shown next to a disabled Dispute button when the booking is overdue. */
export const OVERDUE_DISPUTE_MESSAGE =
  "An overdue booking can't be disputed";

/** Billing item that is NOT eligible for disputes. */
export const PLATFORM_FEE_BILLING_ITEM = "platformfee";

/**
 * Supporting document upload rules.
 */
export const SUPPORTED_FILE_EXTENSIONS = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".docx",
  ".xlsx",
];

export const SUPPORTED_FILE_ACCEPT = SUPPORTED_FILE_EXTENSIONS.join(",");

export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB per file
export const MAX_FILE_SIZE_LABEL = "10MB";
